// src/components/editor/BlurbEditor.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API_BASE } from "../../utils/api";

/**
 * BlurbEditor
 * -----------
 * - Simple text area to author/edit the book blurb during draft phase
 * - Length limited to 600 chars
 * - Saves to PUT /api/drafts/:id/blurb
 * - On success, routes back to the EditorPage (history back first, otherwise explicit /editor/:id)
 */
export default function BlurbEditor() {
  const { id } = useParams(); // draft id
  const navigate = useNavigate();
  const [blurb, setBlurb] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const MAX = 600;

  // Load any existing blurb so author can edit
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const accessToken = localStorage.getItem("rv_access_token");
        if (!accessToken) return;

        const res = await fetch(`${API_BASE}/api/drafts/${id}`, {
          credentials: "omit",
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        });
        if (!res.ok) return;

        const data = await res.json();
        if (!ignore) setBlurb(data?.blurb || "");
      } catch {
        /* no-op */
      }
    })();
    return () => {
      ignore = true;
    };
  }, [id]);

  const goBackToEditor = () => {
    // First try to go back (most robust when navigated from EditorPage)
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      // Fallback to explicit route
      navigate(`/editor/${id}`);
    }
  };

  const save = async () => {
    setError("");
    setSaving(true);
    try {
      const text = (blurb || "").trim();
      if (text.length > MAX) {
        setError(`Blurb is too long (${text.length}/${MAX}).`);
        setSaving(false);
        return;
      }

      const accessToken = localStorage.getItem("rv_access_token");
      if (!accessToken) {
        setError("You are not signed in.");
        setSaving(false);
        return;
      }

      const res = await fetch(`${API_BASE}/api/drafts/${id}/blurb`, {
        method: "PUT",
        credentials: "omit",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ blurb: text }),
      });

      if (!res.ok) {
        const msg = await res.text().catch(() => "");
        setError(msg || "Failed to save blurb.");
        setSaving(false);
        return;
      }

      goBackToEditor();
    } catch (err) {
      console.error(err);
      setError("Failed to save blurb.");
      setSaving(false);
    }
  };
  return (
    <div style={{ maxWidth: 760, margin: "32px auto", padding: "0 16px" }}>
      <h1 style={{ marginBottom: 8 }}>Write Blurb</h1>
      <p style={{ marginTop: 0, color: "#666" }}>
        Short description readers will see in the store and your library
        (max {MAX} characters).
      </p>

      <textarea
        value={blurb}
        onChange={(e) => setBlurb(e.target.value)}
        rows={10}
        style={{
          width: "100%",
          resize: "vertical",
          fontSize: 16,
          lineHeight: 1.5,
          padding: 12,
          borderRadius: 8,
          border: "1px solid #d7d7de",
          outline: "none",
          boxShadow: "inset 0 1px 2px rgba(0,0,0,0.04)",
        }}
        maxLength={MAX}
        placeholder="Enter the book blurb…"
      />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginTop: 10,
        }}
      >
        <span style={{ color: blurb.length > MAX ? "#d00" : "#888" }}>
          {blurb.length}/{MAX}
        </span>

        <div style={{ flex: 1 }} />

        <button
          type="button"
          onClick={goBackToEditor}
          disabled={saving}
          style={{
            background: "transparent",
            border: "1px solid #c9c9d3",
            color: "#333",
            borderRadius: 8,
            padding: "10px 16px",
            cursor: "pointer",
          }}
        >
          Cancel
        </button>

        <button
          type="button"
          onClick={save}
          disabled={saving}
          style={{
            background: saving ? "#9aa6ff" : "#5b6dff",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            padding: "10px 18px",
            fontWeight: 600,
            cursor: saving ? "default" : "pointer",
          }}
        >
          {saving ? "Saving…" : "Save Blurb"}
        </button>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            marginTop: 12,
            background: "#fff7ed",
            border: "1px solid #f3e1c7",
            color: "#8a5a20",
            padding: 12,
            borderRadius: 8,
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}
